import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Loader2, PieChart, TrendingUp, TrendingDown, Target, 
  AlertTriangle, RefreshCw, ArrowUpRight, Zap, DollarSign,
  BarChart3, Calendar, Shield, Rocket, Brain, LineChart,
  CheckCircle, XCircle, Clock, ArrowRight, Activity, Gauge,
  ShieldAlert, Layers, Hourglass
} from "lucide-react";
import AnalysisSections from "../components/AnalysisSections";
import AIAnalysisChat from "../components/AIAnalysisChat";

import { API } from "../config/api";

const ElapsedClock = ({ since }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!since) return null;
  const s = Math.max(0, Math.floor((now - since) / 1000));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return <span className="font-mono">{mm}:{ss}</span>;
};

const PortfolioDesignerPage = () => {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [investmentAmount, setInvestmentAmount] = useState("100000");
  const [targetReturn, setTargetReturn] = useState("40");
  const [maxDrawdown, setMaxDrawdown] = useState("20"); // Max tolerable loss (%)
  const [riskLevel, setRiskLevel] = useState("medium"); // Complete Portfolio risk level
  const [confidenceRange, setConfidenceRange] = useState("0-100"); // Default to all
  const [indexSelection, setIndexSelection] = useState("sp500"); // New: Index Selection
  const [timeHorizon, setTimeHorizon] = useState("long_term"); // New: Time Horizon
  const [sector, setSector] = useState("all"); // Sector filter
  const [subSector, setSubSector] = useState("all"); // Sub-sector filter
  const [usePredictive, setUsePredictive] = useState(true); // Use predictive analytics by default
  const [entryPointTimeframe, setEntryPointTimeframe] = useState("na"); // Entry zone regression timeframe

  // Entry Point Timeframe Options
  const entryPointOptions = [
    { value: "na", label: "N/A" },
    { value: "1d", label: "1 Day" },
    { value: "5d", label: "5 Days" },
    { value: "1mo", label: "1 Month" },
    { value: "3mo", label: "3 Months" },
    { value: "6mo", label: "6 Months" },
    { value: "1y", label: "1 Year" }
  ];

  // Index Selection Options
  const indexOptions = [
    { value: "sp500", label: "S&P 500", description: "500 large-cap U.S. stocks" },
    { value: "nasdaq100", label: "NASDAQ 100", description: "Top 100 NASDAQ stocks" },
    { value: "russell2000", label: "Russell 2000", description: "2,000 small-cap stocks" },
    { value: "dow_jones", label: "Dow Jones Industrial Average", description: "30 blue-chip stocks" },
    { value: "all_stocks", label: "All 7,000+ Stocks", description: "NYSE, NASDAQ, AMEX" },
    { value: "all_etfs", label: "All ETFs", description: "1,500+ ETFs" },
    { value: "complete_portfolio", label: "🧺 Complete Portfolio", description: "AI builds a full multi-asset portfolio — stocks (All Stocks) + ETFs (All ETFs: Fixed Income, Alternatives, Asset Allocation, Commodity, Currency, Equity) tuned to your risk level, target return & max drawdown" },
    { value: "political_trades", label: "🏛️ Political Trades", description: "Tickers from the Congressional Holdings Heatmap (House + Senate PTRs)" },
    { value: "trump_trades", label: "🇺🇸 Trump Trades", description: "Equity & ETF tickers disclosed by President Donald J. Trump and First Lady Melania Trump on OGE Form 278-T" }
  ];

  // Sector Options (GICS Classification)
  const sectorOptions = [
    { value: "all", label: "All Sectors" },
    { value: "energy", label: "Energy" },
    { value: "materials", label: "Materials" },
    { value: "industrials", label: "Industrials" },
    { value: "consumer_discretionary", label: "Consumer Discretionary" },
    { value: "consumer_staples", label: "Consumer Staples" },
    { value: "health_care", label: "Health Care" },
    { value: "financials", label: "Financials" },
    { value: "information_technology", label: "Information Technology" },
    { value: "communication_services", label: "Communication Services" },
    { value: "utilities", label: "Utilities" },
    { value: "real_estate", label: "Real Estate" },
    { value: "global_markets", label: "Global Markets" }
  ];

  // Sub-sector Options by Sector (GICS Industry Groups & Industries)
  const subSectorOptions = {
    energy: [
      { value: "all", label: "All Energy" },
      { value: "energy_equipment_services", label: "Energy Equipment & Services" },
      { value: "oil_gas_consumable_fuels", label: "Oil, Gas & Consumable Fuels" }
    ],
    materials: [
      { value: "all", label: "All Materials" },
      { value: "chemicals", label: "Chemicals" },
      { value: "construction_materials", label: "Construction Materials" },
      { value: "containers_packaging", label: "Containers & Packaging" },
      { value: "metals_mining", label: "Metals & Mining" },
      { value: "paper_forest_products", label: "Paper & Forest Products" }
    ],
    industrials: [
      { value: "all", label: "All Industrials" },
      { value: "aerospace_defense", label: "Aerospace & Defense" },
      { value: "building_products", label: "Building Products" },
      { value: "construction_engineering", label: "Construction & Engineering" },
      { value: "electrical_equipment", label: "Electrical Equipment" },
      { value: "industrial_conglomerates", label: "Industrial Conglomerates" },
      { value: "machinery", label: "Machinery" },
      { value: "trading_companies_distributors", label: "Trading Companies & Distributors" },
      { value: "commercial_services_supplies", label: "Commercial Services & Supplies" },
      { value: "professional_services", label: "Professional Services" },
      { value: "air_freight_logistics", label: "Air Freight & Logistics" },
      { value: "airlines", label: "Airlines" },
      { value: "marine", label: "Marine" },
      { value: "road_rail", label: "Road & Rail" },
      { value: "transportation_infrastructure", label: "Transportation Infrastructure" }
    ],
    consumer_discretionary: [
      { value: "all", label: "All Consumer Discretionary" },
      { value: "auto_components", label: "Auto Components" },
      { value: "automobiles", label: "Automobiles" },
      { value: "household_durables", label: "Household Durables" },
      { value: "leisure_products", label: "Leisure Products" },
      { value: "textiles_apparel_luxury", label: "Textiles, Apparel & Luxury Goods" },
      { value: "hotels_restaurants_leisure", label: "Hotels, Restaurants & Leisure" },
      { value: "diversified_consumer_services", label: "Diversified Consumer Services" },
      { value: "distributors", label: "Distributors" },
      { value: "internet_direct_marketing_retail", label: "Internet & Direct Marketing Retail" },
      { value: "multiline_retail", label: "Multiline Retail" },
      { value: "specialty_retail", label: "Specialty Retail" }
    ],
    consumer_staples: [
      { value: "all", label: "All Consumer Staples" },
      { value: "food_staples_retailing", label: "Food & Staples Retailing" },
      { value: "beverages", label: "Beverages" },
      { value: "food_products", label: "Food Products" },
      { value: "tobacco", label: "Tobacco" },
      { value: "household_products", label: "Household Products" },
      { value: "personal_products", label: "Personal Products" }
    ],
    health_care: [
      { value: "all", label: "All Health Care" },
      { value: "health_care_equipment_supplies", label: "Health Care Equipment & Supplies" },
      { value: "health_care_providers_services", label: "Health Care Providers & Services" },
      { value: "health_care_technology", label: "Health Care Technology" },
      { value: "biotechnology", label: "Biotechnology" },
      { value: "pharmaceuticals", label: "Pharmaceuticals" },
      { value: "life_sciences_tools_services", label: "Life Sciences Tools & Services" }
    ],
    financials: [
      { value: "all", label: "All Financials" },
      { value: "banks", label: "Banks" },
      { value: "thrifts_mortgage_finance", label: "Thrifts & Mortgage Finance" },
      { value: "diversified_financial_services", label: "Diversified Financial Services" },
      { value: "consumer_finance", label: "Consumer Finance" },
      { value: "capital_markets", label: "Capital Markets" },
      { value: "insurance", label: "Insurance" },
      { value: "mortgage_reits", label: "Mortgage Real Estate Investment Trusts (REITs)" }
    ],
    information_technology: [
      { value: "all", label: "All Information Technology" },
      { value: "it_services", label: "IT Services" },
      { value: "software", label: "Software" },
      { value: "communications_equipment", label: "Communications Equipment" },
      { value: "technology_hardware_storage", label: "Technology Hardware, Storage & Peripherals" },
      { value: "electronic_equipment_instruments", label: "Electronic Equipment, Instruments & Components" },
      { value: "semiconductors", label: "Semiconductors & Semiconductor Equipment" }
    ],
    communication_services: [
      { value: "all", label: "All Communication Services" },
      { value: "diversified_telecom", label: "Diversified Telecommunication Services" },
      { value: "wireless_telecom", label: "Wireless Telecommunication Services" },
      { value: "media", label: "Media" },
      { value: "entertainment", label: "Entertainment" },
      { value: "interactive_media_services", label: "Interactive Media & Services" }
    ],
    utilities: [
      { value: "all", label: "All Utilities" },
      { value: "electric_utilities", label: "Electric Utilities" },
      { value: "gas_utilities", label: "Gas Utilities" },
      { value: "multi_utilities", label: "Multi-Utilities" },
      { value: "water_utilities", label: "Water Utilities" },
      { value: "independent_power_renewable", label: "Independent Power and Renewable Electricity Producers" }
    ],
    real_estate: [
      { value: "all", label: "All Real Estate" },
      { value: "equity_reits", label: "Equity Real Estate Investment Trusts (REITs)" },
      { value: "real_estate_management_development", label: "Real Estate Management & Development" }
    ],
    global_markets: [
      { value: "all", label: "All Global Markets" },
      { value: "asia_pacific", label: "Asia Pacific" },
      { value: "europe", label: "Europe (Major)" },
      { value: "europe_other", label: "Europe (Other)" },
      { value: "africa_middle_east", label: "Africa & Middle East" },
      { value: "latin_america", label: "Latin America" },
      { value: "north_america", label: "North America" }
    ]
  };

  // Reset sub-sector when sector changes; auto-switch to ETFs for Global Markets
  const handleSectorChange = (newSector) => {
    setSector(newSector);
    setSubSector("all");
    if (newSector === 'global_markets') {
      setIndexSelection('all_etfs');
    }
  };

  // Time Horizon Options with Algorithm descriptions
  const timeHorizonOptions = [
    { 
      value: "intraday", 
      label: "Intraday", 
      timeRange: "Minutes to Hours",
      algorithm: "RF/XGBoost + LSTM/CNN",
      description: "Tree-based models (Random Forest, XGBoost) on order-book features, LSTM/CNN on 1-5 min bars"
    },
    { 
      value: "swing", 
      label: "Swing", 
      timeRange: "2-20 Days",
      algorithm: "ARIMA + Transformers",
      description: "ARIMA/VAR + ML classifiers (LR, RF, XGBoost), Transformer architectures for 60%+ accuracy"
    },
    { 
      value: "long_term", 
      label: "Long-Term", 
      timeRange: "6-12 Months",
      algorithm: "Factor Models + Hybrid ML",
      description: "Cross-sectional regressions, yield-growth-valuation frameworks, LASSO/Ridge on fundamentals"
    }
  ];

  const confidenceOptions = [
    { value: "0-100", label: "All (0-100%)" },
    { value: "25-100", label: "25-100%" },
    { value: "50-100", label: "50-100%" },
    { value: "75-100", label: "75-100%" }
  ];

  const riskLevelOptions = [
    { value: "off", label: "Off — no risk template", description: "Pure score-driven: chase the target return within your max drawdown, ignoring sleeve templates" },
    { value: "low", label: "Low Risk", description: "Capital preservation — heavy Fixed Income & Asset Allocation, small stock sleeve" },
    { value: "medium", label: "Medium Risk", description: "Balanced — diversified ETFs with a meaningful stock sleeve" },
    { value: "high", label: "High Risk", description: "Growth — stock-heavy with ETF sleeves for diversification" }
  ];

  const isCompletePortfolio = indexSelection === "complete_portfolio";

  const [scanProgress, setScanProgress] = useState(null); // { progress, total }
  const [scanStartedAt, setScanStartedAt] = useState(null);

  const generatePortfolio = useCallback(async () => {
    setLoading(true);
    setPortfolio(null);
    setScanProgress(null);
    setScanStartedAt(Date.now());
    try {
      const endpoint = usePredictive ? `${API}/portfolio/design-predictive` : `${API}/portfolio/design`;
      
      const response = await axios.post(endpoint, {
        investment_amount: parseFloat(investmentAmount),
        target_return: parseFloat(targetReturn),
        max_drawdown: parseFloat(maxDrawdown) || 20,
        risk_level: riskLevel,
        index_selection: indexSelection,
        time_horizon: timeHorizon,
        confidence_range: confidenceRange,
        sector: sector,
        sub_sector: subSector,
        entry_point_timeframe: entryPointTimeframe
      });

      const data = response.data;

      // Background task pattern - poll for results
      if (data.status === "scanning" && data.task_id) {
        setScanProgress({ progress: 0, total: data.total_universe || 100 });
        toast.info(`Scanning ${data.total_universe} stocks in background...`);
        
        // Poll every 5 seconds
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API}/portfolio/scan-status/${data.task_id}`);
            const statusData = statusRes.data;
            
            setScanProgress({ progress: statusData.progress || 0, total: statusData.total || 100 });
            
            if (statusData.status === "complete" && statusData.result) {
              clearInterval(pollInterval);
              setPortfolio(statusData.result);
              setScanProgress(null);
              setLoading(false);
              const count = statusData.result.holdings?.length || 0;
              toast.success(`Portfolio: ${count} positions selected`);
            } else if (statusData.status === "error") {
              clearInterval(pollInterval);
              setScanProgress(null);
              setLoading(false);
              toast.error(statusData.message || "Scan failed");
            }
          } catch (pollErr) {
            console.error("Poll error:", pollErr);
          }
        }, 5000);
        
        return; // Don't setLoading(false) yet
      }

      // Synchronous result (small universe)
      setPortfolio(data);
      const indexLabel = indexOptions.find(i => i.value === indexSelection)?.label || indexSelection;
      const sectorLabel = sector !== 'all' ? sectorOptions.find(s => s.value === sector)?.label : 'All Sectors';
      toast.success(`Portfolio: ${data.holdings?.length || 0} positions from ${indexLabel} - ${sectorLabel}`);
      setLoading(false);
    } catch (error) {
      console.error("Error generating portfolio:", error);
      toast.error("Failed to generate portfolio. Please try again.");
      setLoading(false);
    }
  }, [investmentAmount, targetReturn, maxDrawdown, riskLevel, indexSelection, timeHorizon, confidenceRange, sector, subSector, usePredictive, entryPointTimeframe]);

  // Removed auto-generate on mount - user must click the button to start

  const getChangeColor = (val) => {
    if (val > 0) return "text-green-600";
    if (val < 0) return "text-red-600";
    return "text-slate-600";
  };

  const getAllocationColor = (weight) => {
    if (weight >= 15) return "bg-indigo-600";
    if (weight >= 10) return "bg-blue-500";
    if (weight >= 5) return "bg-green-500";
    return "bg-slate-400";
  };

  const getRiskBadge = (risk) => {
    const colors = {
      'LOW': 'bg-green-100 text-green-700',
      'MODERATE': 'bg-yellow-100 text-yellow-700',
      'HIGH': 'bg-red-100 text-red-700'
    };
    return colors[risk] || colors['MODERATE'];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600">
                <PieChart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  Portfolio Designer
                </h1>
                <p className="text-xs text-slate-500">AI-Powered 40%+ Annual Returns</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <a 
                href="/" 
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1" target="_blank" rel="noopener noreferrer">
                <ArrowUpRight className="w-4 h-4" />
                Dashboard
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Analysis Sections Navigation */}
        <AnalysisSections currentPage="portfolio" />

        {/* Configuration Panel */}
        <Card className="border-purple-200 bg-gradient-to-br from-white to-purple-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Brain className="w-5 h-5 text-purple-600" />
              Portfolio Configuration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Investment ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                  <Input
                    type="number"
                    value={investmentAmount}
                    onChange={(e) => setInvestmentAmount(e.target.value)}
                    className="pl-7 h-9 text-sm"
                    placeholder="100000"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Target Return (%)</label>
                <div className="relative">
                  <TrendingUp className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                  <Input
                    type="number"
                    value={targetReturn}
                    onChange={(e) => setTargetReturn(e.target.value)}
                    className="pl-7 h-9 text-sm"
                    placeholder="40"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Max Drawdown (%)</label>
                <div className="relative">
                  <ShieldAlert className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-red-400" />
                  <Input
                    type="number"
                    value={maxDrawdown}
                    onChange={(e) => setMaxDrawdown(e.target.value)}
                    className="pl-7 h-9 text-sm border-red-200"
                    placeholder="20"
                    data-testid="max-drawdown-input"
                  />
                </div>
                <p className="text-[10px] text-red-500 mt-1">Max loss you'd tolerate</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Index Selection</label>
                <select
                  value={indexSelection}
                  onChange={(e) => setIndexSelection(e.target.value)}
                  className="w-full h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                  data-testid="index-select"
                >
                  {indexOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">
                  {indexOptions.find(o => o.value === indexSelection)?.description || ''}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Time Horizon</label>
                <select
                  value={timeHorizon}
                  onChange={(e) => setTimeHorizon(e.target.value)}
                  className="w-full h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                  data-testid="time-horizon-select"
                >
                  {timeHorizonOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label} ({opt.timeRange})</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">
                  {timeHorizonOptions.find(o => o.value === timeHorizon)?.algorithm || ''}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Confidence Interval</label>
                <select
                  value={confidenceRange}
                  onChange={(e) => setConfidenceRange(e.target.value)}
                  className="w-full h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                  data-testid="confidence-select"
                >
                  {confidenceOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">
                  {confidenceRange === '75-100' ? 'High confidence stocks only' :
                   confidenceRange === '50-100' ? 'Medium-high confidence' :
                   confidenceRange === '25-100' ? 'Include moderate confidence' :
                   'All confidence levels'}
                </p>
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={generatePortfolio}
                  disabled={loading}
                  className="w-full h-9 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  data-testid="generate-portfolio-btn"
                >
                  {loading ? (
                    <>
                      <Hourglass className="w-4 h-4 mr-1.5 animate-spin" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-1.5" />
                      Scan Market
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Risk Level Row — Complete Portfolio only */}
            {isCompletePortfolio ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-200">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Risk Level</label>
                  <select
                    value={riskLevel}
                    onChange={(e) => setRiskLevel(e.target.value)}
                    className="w-full h-9 px-2 rounded-md border border-purple-300 bg-white text-sm"
                    data-testid="risk-level-select"
                  >
                    {riskLevelOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-purple-600 mt-1">
                    {riskLevelOptions.find(o => o.value === riskLevel)?.description}
                  </p>
                </div>
                <div className="md:col-span-2 flex items-center">
                  <p className="text-xs text-slate-600 bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <Layers className="w-3.5 h-3.5 inline mr-1 text-purple-600" />
                    <span className="font-semibold text-purple-700">Complete Portfolio</span> pulls stocks from All Stocks and ETFs from All ETFs
                    (Fixed Income, Alternatives, Asset Allocation, Commodity, Currency, Equity), then AI adjusts the mix for
                    <span className="font-semibold"> {riskLevelOptions.find(o => o.value === riskLevel)?.label}</span> to hit
                    <span className="font-semibold"> {targetReturn}%</span> while keeping drawdown under
                    <span className="font-semibold text-red-600"> {maxDrawdown}%</span>.
                  </p>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-200">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Sector</label>
                <select
                  value={sector}
                  onChange={(e) => handleSectorChange(e.target.value)}
                  className="w-full h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                  data-testid="sector-select"
                >
                  {sectorOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {sector !== 'all' && subSectorOptions[sector] && (
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Sub-Sector</label>
                  <select
                    value={subSector}
                    onChange={(e) => setSubSector(e.target.value)}
                    className="w-full h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                    data-testid="sub-sector-select"
                  >
                    {subSectorOptions[sector].map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Entry Point Timeframe</label>
                <select
                  value={entryPointTimeframe}
                  onChange={(e) => setEntryPointTimeframe(e.target.value)}
                  className="w-full h-9 px-2 rounded-md border border-green-200 bg-white text-sm"
                  data-testid="entry-point-timeframe-select"
                >
                  {entryPointOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-[10px] text-green-600 mt-1">
                  {entryPointTimeframe === 'na' ? 'Entry zone filter disabled' : 'Entry zone regression window'}
                </p>
              </div>
              {sector !== 'all' && (
                <div className="flex items-center">
                  <p className="text-xs text-indigo-600">
                    <span className="font-medium">Filtering:</span> {sectorOptions.find(s => s.value === sector)?.label}
                    {subSector !== 'all' && ` → ${subSectorOptions[sector]?.find(s => s.value === subSector)?.label}`}
                  </p>
                </div>
              )}
            </div>
            )}

            {/* Algorithm Description Panel */}
            {usePredictive && !isCompletePortfolio && (
              <div className="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                <div className="flex items-start gap-3">
                  <Activity className="w-5 h-5 text-indigo-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-indigo-800 text-sm">
                      {timeHorizon === 'intraday' && 'Intraday Algorithm: RF/XGBoost + LSTM/CNN'}
                      {timeHorizon === 'swing' && 'Swing Algorithm: ARIMA + Transformer Networks'}
                      {timeHorizon === 'long_term' && 'Long-Term Algorithm: Factor Models + Hybrid ML'}
                    </h4>
                    <p className="text-xs text-slate-600 mt-1">
                      Scanning <span className="font-semibold text-indigo-700">{indexOptions.find(i => i.value === indexSelection)?.label}</span>
                      {sector !== 'all' && (
                        <span className="text-purple-600"> → {sectorOptions.find(s => s.value === sector)?.label}
                          {subSector !== 'all' && ` → ${subSectorOptions[sector]?.find(s => s.value === subSector)?.label}`}
                        </span>
                      )}
                      {' '}for {targetReturn}%+ predicted return within{' '}
                      <span className="font-semibold">{timeHorizonOptions.find(h => h.value === timeHorizon)?.timeRange}</span>.
                      {entryPointTimeframe !== 'na' ? (
                        <>{' '}Entry zone: <span className="font-semibold text-green-600">{entryPointOptions.find(o => o.value === entryPointTimeframe)?.label}</span> regression.</>
                      ) : (
                        <>{' '}<span className="text-slate-400">Entry zone filter: off</span></>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      <span className="font-medium">Methodology:</span> {timeHorizonOptions.find(h => h.value === timeHorizon)?.description}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Portfolio Sanity-Check Chat — moved to top, under the config card */}
        {portfolio && portfolio.holdings?.length > 0 && (
          <AIAnalysisChat
            mode="portfolio"
            title="Portfolio Sanity Check — AI Chat"
            extraContext={{
              portfolio: {
                index_selection: portfolio.index_selection || indexSelection,
                risk_level: portfolio.risk_level || null,
                target_return_pct: parseFloat(targetReturn),
                max_drawdown_tolerance_pct: parseFloat(maxDrawdown) || 20,
                projected_return_pct: portfolio.portfolio_predicted_return,
                confidence_pct: portfolio.portfolio_confidence || portfolio.avg_confidence,
                portfolio_max_drawdown_pct: portfolio.portfolio_max_drawdown || null,
                total_invested: portfolio.total_investment,
                holdings: (portfolio.holdings || []).slice(0, 100).map(h => ({
                  ticker: h.ticker,
                  weight_pct: h.weight,
                  predicted_return_pct: h.predicted_return,
                  confidence_pct: h.confidence,
                  asset_class: h.asset_class || h.sector,
                  max_drawdown_pct: h.max_drawdown,
                })),
              },
            }}
          />
        )}

        {/* Loading State */}
        {loading && !portfolio && (
          <Card className="border-purple-200">
            <CardContent className="p-4">
              <div className="animate-pulse space-y-3">
                {/* Summary cards skeleton */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {Array.from({length:6}).map((_,i) => (
                    <div key={i} className="bg-slate-100 rounded-lg p-3">
                      <div className="bg-slate-200 rounded h-3 w-1/2 mb-2" />
                      <div className="bg-slate-200 rounded h-6 w-2/3" />
                    </div>
                  ))}
                </div>
                {/* Table skeleton */}
                <div className="bg-slate-50 rounded px-4 py-2.5 flex gap-4">
                  {[1,2,3,4,5,6].map(i => <div key={i} className="bg-slate-200 rounded h-3 flex-1" />)}
                </div>
                {Array.from({length:8}).map((_,r) => (
                  <div key={r} className="px-4 py-2.5 flex gap-4 border-t border-slate-100">
                    {[1,2,3,4,5,6].map(c => <div key={c} className="bg-slate-200 rounded h-3 flex-1" />)}
                  </div>
                ))}
              </div>
              <div className="text-center mt-4">
                <div className="flex items-center justify-center gap-2 mb-1" data-testid="scan-hourglass">
                  <Hourglass className="w-6 h-6 text-purple-600 animate-spin" style={{ animationDuration: '2.5s' }} />
                  <span className="text-purple-700 font-semibold text-sm">
                    Still working… <ElapsedClock since={scanStartedAt} /> elapsed
                  </span>
                </div>
                <p className="text-slate-600 font-medium">
                  Scanning {indexOptions.find(i => i.value === indexSelection)?.label}...
                </p>
                {scanProgress && (
                  <div className="mt-4 w-64 mx-auto">
                    <div className="w-full bg-slate-200 rounded-full h-2.5">
                      <div 
                        className="bg-purple-600 h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${Math.round((scanProgress.progress / scanProgress.total) * 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {scanProgress.progress} / {scanProgress.total} stocks analyzed
                    </p>
                  </div>
                )}
                <p className="text-slate-500 text-sm mt-2">
                  Analyzing stocks for {timeHorizonOptions.find(h => h.value === timeHorizon)?.timeRange} predictions with {targetReturn}%+ returns
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Portfolio Results */}
        {portfolio && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <Card className="border-green-200 bg-gradient-to-br from-white to-green-50">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Projected Return
                  </p>
                  <p className="text-2xl font-bold text-green-700">
                    {(portfolio.portfolio_predicted_return || portfolio.projected_return || 0).toFixed?.(2) || 0}%
                  </p>
                  <p className="text-xs text-green-600">{portfolio.time_frame_label || 'Annual'}</p>
                </CardContent>
              </Card>
              <Card className="border-cyan-200 bg-gradient-to-br from-white to-cyan-50">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> System Confidence
                  </p>
                  <p className="text-2xl font-bold text-cyan-700">
                    {portfolio.portfolio_confidence || portfolio.avg_confidence || 0}%
                  </p>
                  <p className="text-xs text-cyan-600">Target Achievement</p>
                </CardContent>
              </Card>
              <Card className="border-orange-200 bg-gradient-to-br from-white to-orange-50">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <LineChart className="w-3 h-3" /> Historical Avg
                  </p>
                  <p className="text-2xl font-bold text-orange-700">
                    +{(portfolio.avg_historical_return || 0).toFixed?.(1) || 0}%
                  </p>
                  <p className="text-xs text-orange-600">{portfolio.time_frame_label || '1Y'} Returns</p>
                </CardContent>
              </Card>
              <Card className="border-purple-200 bg-gradient-to-br from-white to-purple-50">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Invested
                  </p>
                  <p className="text-2xl font-bold text-purple-700">
                    ${(portfolio.total_investment || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-purple-600">of ${(portfolio.investment_amount || 0).toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-gradient-to-br from-white to-blue-50">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Rocket className="w-3 h-3" /> Expected Value
                  </p>
                  <p className="text-2xl font-bold text-blue-700">
                    ${(portfolio.expected_value || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-blue-600">{portfolio.time_frame_label || '1Y'}</p>
                </CardContent>
              </Card>
              <Card className="border-indigo-200 bg-gradient-to-br from-white to-indigo-50">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <BarChart3 className="w-3 h-3" /> Positions
                  </p>
                  <p className="text-2xl font-bold text-indigo-700">
                    {portfolio.holdings?.length || 0}
                  </p>
                  <p className="text-xs text-indigo-600">of {portfolio.filter_summary?.stocks_scanned || 0} scanned</p>
                </CardContent>
              </Card>
            </div>

            {/* Filter Summary */}
            {portfolio.filter_summary && (
              <Card className="border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-sm">
                          <span className="font-semibold text-green-700">{portfolio.filter_summary.stocks_passed}</span>
                          <span className="text-slate-500"> stocks passed filters</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        <span className="text-sm">
                          <span className="font-semibold text-red-600">{portfolio.filter_summary.stocks_excluded}</span>
                          <span className="text-slate-500"> excluded</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="text-xs text-slate-600 border-slate-300">
                        Filter: {portfolio.filter_summary.filter_criteria}
                      </Badge>
                      {portfolio.filter_summary.data_source && (
                        <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
                          Source: {portfolio.filter_summary.data_source}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Strategy Summary */}
            <Card className="border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-purple-800">
                  <Brain className="w-5 h-5" />
                  AI Strategy Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line">
                  {portfolio.strategy_summary}
                </p>
              </CardContent>
            </Card>

            {/* Complete Portfolio Section */}
            {portfolio.complete_portfolio && (
              <Card className="border-purple-300" data-testid="complete-portfolio-section">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg flex-wrap">
                    <Layers className="w-5 h-5 text-purple-600" />
                    Complete Portfolio
                    <Badge className="bg-purple-600">{portfolio.risk_label}</Badge>
                    {portfolio.ai_adjusted && (
                      <Badge className="bg-emerald-600">
                        <Brain className="w-3 h-3 mr-1" /> AI Sanity-Checked
                      </Badge>
                    )}
                    {portfolio.sanity_check?.passed && (
                      <Badge className="bg-green-600" data-testid="sanity-passed-badge">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Sanity Check Passed{portfolio.sanity_check.iterations > 1 ? ` (${portfolio.sanity_check.iterations} passes)` : ''}
                      </Badge>
                    )}
                    <span className="ml-auto text-xs font-normal text-slate-500 flex items-center gap-1">
                      <ShieldAlert className="w-3.5 h-3.5 text-red-500" />
                      Portfolio Max Drawdown:{' '}
                      <span className={`font-bold ${(portfolio.portfolio_max_drawdown || 0) <= (portfolio.max_drawdown_target || 20) ? 'text-green-600' : 'text-red-600'}`}>
                        {portfolio.portfolio_max_drawdown}%
                      </span>
                      <span className="text-slate-400">/ {portfolio.max_drawdown_target}% tolerance</span>
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Asset Class Breakdown */}
                  {portfolio.asset_class_breakdown?.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                      {portfolio.asset_class_breakdown.map((b) => (
                        <div key={b.sleeve} className="p-2.5 bg-purple-50 rounded-lg border border-purple-100" data-testid={`sleeve-${b.sleeve.replace(/\s+/g, '-').toLowerCase()}`}>
                          <p className="text-[10px] text-slate-500 truncate">{b.sleeve}</p>
                          <p className="text-lg font-bold text-purple-700">{b.weight_pct}%</p>
                          <div className="w-full bg-purple-100 rounded-full h-1.5 mt-1">
                            <div className="bg-purple-600 h-1.5 rounded-full" style={{ width: `${Math.min(100, b.weight_pct)}%` }} />
                          </div>
                          <p className="text-[9px] text-slate-400 mt-1">{b.holdings} holding{b.holdings !== 1 ? 's' : ''}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* AI Flags */}
                  {portfolio.ai_flagged?.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1" data-testid="ai-flagged-list">
                      <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" /> AI Review Notes
                      </p>
                      {portfolio.ai_flagged.map((f, i) => (
                        <p key={i} className="text-xs text-amber-700">• {f}</p>
                      ))}
                    </div>
                  )}

                  {/* Holdings Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="complete-portfolio-table">
                      <thead>
                        <tr className="bg-slate-100 text-slate-700">
                          <th className="text-left py-2.5 px-3 font-semibold">Ticker</th>
                          <th className="text-left py-2.5 px-3 font-semibold hidden md:table-cell">Asset Class</th>
                          <th className="text-center py-2.5 px-3 font-semibold">Weight</th>
                          <th className="text-center py-2.5 px-3 font-semibold">Price</th>
                          <th className="text-center py-2.5 px-3 font-semibold">Exp Return (1Y)</th>
                          <th className="text-center py-2.5 px-3 font-semibold hidden lg:table-cell">1Y Return</th>
                          <th className="text-center py-2.5 px-3 font-semibold">Max DD</th>
                          <th className="text-center py-2.5 px-3 font-semibold">Shares</th>
                          <th className="text-center py-2.5 px-3 font-semibold">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {portfolio.holdings?.map((h) => (
                          <tr key={h.ticker} className="border-b border-slate-100 hover:bg-purple-50">
                            <td className="py-2.5 px-3">
                              <div className="flex flex-col">
                                <a href={`/stock?ticker=${h.ticker}`} className="font-bold text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">
                                  {h.ticker}
                                </a>
                                <span className="text-[10px] text-slate-400 max-w-[180px] truncate">{h.company_name}</span>
                                {h.ai_note && <span className="text-[9px] text-emerald-600 italic">{h.ai_note}</span>}
                              </div>
                            </td>
                            <td className="py-2.5 px-3 hidden md:table-cell">
                              <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-700">{h.asset_class}</Badge>
                            </td>
                            <td className="py-2.5 px-3 text-center font-semibold">{h.weight}%</td>
                            <td className="py-2.5 px-3 text-center">${h.current_price?.toFixed?.(2)}</td>
                            <td className="py-2.5 px-3 text-center">
                              <div className="flex flex-col items-center">
                                <span className={`font-bold ${h.predicted_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {h.predicted_return >= 0 ? '+' : ''}{h.predicted_return?.toFixed?.(1)}%
                                </span>
                                {h.raw_predicted_return != null && (
                                  <span className="text-[9px] text-amber-600" title="AI sanity check calibrated this prediction to a realistic level" data-testid={`calibrated-${h.ticker}`}>
                                    <s className="text-slate-400">{h.raw_predicted_return >= 0 ? '+' : ''}{h.raw_predicted_return?.toFixed?.(1)}%</s> calibrated
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2.5 px-3 text-center hidden lg:table-cell">
                              <span className={h.ret_1y >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {h.ret_1y >= 0 ? '+' : ''}{h.ret_1y?.toFixed?.(1)}%
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className={`font-medium ${h.max_drawdown <= (portfolio.max_drawdown_target || 20) ? 'text-slate-600' : 'text-red-600'}`}>
                                -{h.max_drawdown?.toFixed?.(1)}%
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-center">{h.shares}</td>
                            <td className="py-2.5 px-3 text-center font-semibold">${h.value?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Holdings Table */}
            {!portfolio.complete_portfolio && (
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PieChart className="w-5 h-5 text-indigo-600" />
                  Portfolio Holdings
                  <Badge variant="outline" className="ml-2">
                    {portfolio.holdings?.length || 0} positions
                  </Badge>
                  {portfolio.methodology && (
                    <Badge className="ml-2 bg-indigo-600">
                      <Activity className="w-3 h-3 mr-1" />
                      Predictive Analytics
                    </Badge>
                  )}
                  {portfolio.sanity_check?.passed && (
                    <Badge className="ml-2 bg-green-600" data-testid="scan-sanity-badge">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Sanity Check Passed{portfolio.sanity_check.iterations > 1 ? ` (${portfolio.sanity_check.iterations} passes)` : ''}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {portfolio.ai_flagged?.length > 0 && (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-1" data-testid="scan-ai-flags">
                    <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" /> AI Sanity Check Notes
                    </p>
                    {portfolio.ai_flagged.map((f, i) => (
                      <p key={i} className="text-xs text-amber-700">• {f}</p>
                    ))}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700">
                        <th className="text-left py-3 px-3 font-semibold">Ticker</th>
                        <th className="text-center py-3 px-3 font-semibold">Weight</th>
                        <th className="text-center py-3 px-3 font-semibold">Price</th>
                        {portfolio.methodology ? (
                          <>
                            <th className="text-center py-3 px-3 font-semibold">Predicted {portfolio.time_frame_label || '1Y'}</th>
                            <th className="text-center py-3 px-3 font-semibold text-red-700">Max DD</th>
                            <th className="text-center py-3 px-3 font-semibold">Confidence</th>
                            <th className="text-center py-3 px-3 font-semibold">Signal</th>
                            <th className="text-center py-3 px-3 font-semibold">Entry Zone</th>
                            <th className="text-center py-3 px-3 font-semibold">Exit Zone</th>
                            <th className="text-center py-3 px-3 font-semibold hidden lg:table-cell">Candles</th>
                          </>
                        ) : (
                          <>
                            <th className="text-center py-3 px-3 font-semibold">Analyst Target</th>
                            <th className="text-center py-3 px-3 font-semibold">1Y Return</th>
                          </>
                        )}
                        <th className="text-center py-3 px-3 font-semibold">Shares</th>
                        <th className="text-center py-3 px-3 font-semibold">Value</th>
                        <th className="text-center py-3 px-3 font-semibold hidden lg:table-cell">Day Chg</th>
                        {portfolio.methodology && (
                          <th className="text-center py-3 px-3 font-semibold hidden xl:table-cell">Momentum</th>
                        )}
                        <th className="text-center py-3 px-3 font-semibold text-emerald-700">Option ◊</th>
                      </tr>
                    </thead>
                    <tbody>
                      {portfolio.holdings?.map((holding, idx) => (
                        <tr 
                          key={holding.ticker}
                          className={`border-b border-slate-100 hover:bg-indigo-50 ${
                            idx < 3 ? 'bg-purple-50' : ''
                          }`}
                        >
                          <td className="py-3 px-3">
                            <div className="flex flex-col">
                              <a 
                                href={`/stock?ticker=${holding.ticker}`}
                                className="font-bold text-indigo-600 hover:text-indigo-800 hover:underline" target="_blank" rel="noopener noreferrer">
                                {holding.ticker}
                              </a>
                              <span className="text-[10px] text-slate-400">{holding.sector}</span>
                              {holding.ai_note && (
                                <span className="text-[9px] text-emerald-600 italic">{holding.ai_note}</span>
                              )}
                              {(holding.above_sma200 || holding.above_sma50) && (
                                <span className="text-[9px] text-green-600 flex items-center gap-0.5">
                                  <TrendingUp className="w-2.5 h-2.5" /> Above SMA50
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className={`w-10 h-2 rounded-full ${getAllocationColor(holding.weight)}`}>
                                <div 
                                  className="h-full bg-white/30 rounded-full"
                                  style={{ width: `${100 - holding.weight}%`, marginLeft: 'auto' }}
                                />
                              </div>
                              <span className="font-semibold text-xs">{holding.weight}%</span>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-center font-medium">
                            ${holding.current_price?.toFixed?.(2) || "0.00"}
                          </td>
                          {portfolio.methodology ? (
                            <>
                              {/* Predicted Return */}
                              <td className="py-3 px-3 text-center">
                                <div className="flex flex-col items-center">
                                  <span className={`font-bold ${
                                    holding.predicted_return >= targetReturn ? 'text-green-600' : 
                                    holding.predicted_return >= 5 ? 'text-blue-600' : 'text-orange-600'
                                  }`}>
                                    +{holding.predicted_return?.toFixed?.(2) || "0"}%
                                  </span>
                                  {holding.raw_predicted_return != null && (
                                    <span className="text-[9px] text-amber-600" title="AI sanity check calibrated this prediction to a realistic level" data-testid={`calibrated-${holding.ticker}`}>
                                      <s className="text-slate-400">+{holding.raw_predicted_return?.toFixed?.(1)}%</s> calibrated
                                    </span>
                                  )}
                                  <span className="text-[9px] text-slate-400">{portfolio.time_frame_label || '1Y'} Prediction</span>
                                </div>
                              </td>
                              {/* Max Drawdown */}
                              <td className="py-3 px-3 text-center" data-testid={`holding-dd-${holding.ticker}`}>
                                <span className={`font-semibold ${
                                  (holding.max_drawdown || 0) > parseFloat(maxDrawdown || 20) ? 'text-red-600' :
                                  (holding.max_drawdown || 0) >= 10 ? 'text-orange-600' : 'text-slate-600'
                                }`}>
                                  -{holding.max_drawdown?.toFixed?.(1) || "0.0"}%
                                </span>
                              </td>
                              {/* Confidence */}
                              <td className="py-3 px-3 text-center">
                                <div className="flex flex-col items-center">
                                  <div className="flex items-center gap-1">
                                    <Gauge className={`w-3 h-3 ${
                                      holding.confidence >= 75 ? 'text-green-500' : 
                                      holding.confidence >= 50 ? 'text-yellow-500' : 'text-red-500'
                                    }`} />
                                    <span className={`font-semibold ${
                                      holding.confidence >= 75 ? 'text-green-600' : 
                                      holding.confidence >= 50 ? 'text-yellow-600' : 'text-red-600'
                                    }`}>
                                      {holding.confidence?.toFixed?.(0) || "0"}%
                                    </span>
                                  </div>
                                </div>
                              </td>
                              {/* Signal Strength */}
                              <td className="py-3 px-3 text-center">
                                <Badge className={
                                  holding.signal_strength >= 70 ? 'bg-green-600' : 
                                  holding.signal_strength >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                }>
                                  {holding.signal_strength >= 70 ? 'STRONG' : 
                                   holding.signal_strength >= 50 ? 'MODERATE' : 'WEAK'}
                                </Badge>
                              </td>
                              {/* Entry Zone */}
                              <td className="py-3 px-3 text-center">
                                {holding.entry_zone ? (
                                  <div className="flex flex-col items-center">
                                    <span className="font-semibold text-green-600 text-xs">
                                      ${holding.entry_zone.entry_price_low?.toFixed(2)} – ${holding.entry_zone.entry_price_high?.toFixed(2)}
                                    </span>
                                    <span className="text-[9px] text-slate-400">{holding.entry_zone.timeframe || entryPointTimeframe}</span>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-xs">—</span>
                                )}
                              </td>
                              {/* Exit Zone */}
                              <td className="py-3 px-3 text-center">
                                {holding.entry_zone ? (
                                  <div className="flex flex-col items-center">
                                    <span className="font-semibold text-red-500 text-xs">
                                      ${holding.entry_zone.exit_price_low?.toFixed(2)} – ${holding.entry_zone.exit_price_high?.toFixed(2)}
                                    </span>
                                    <span className="text-[9px] text-slate-400">Target</span>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-xs">—</span>
                                )}
                              </td>
                              {/* Candlestick Pattern */}
                              <td className="py-3 px-3 text-center hidden lg:table-cell">
                                <div className="flex flex-col items-center">
                                  {holding.candlestick_bullish === true ? (
                                    <Badge className="bg-green-600 text-[10px]">
                                      BULLISH
                                    </Badge>
                                  ) : holding.candlestick_bullish === false ? (
                                    <Badge className="bg-red-500 text-[10px]">
                                      BEARISH
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-slate-400 text-[10px]">
                                      NEUTRAL
                                    </Badge>
                                  )}
                                  {holding.candlestick_pattern && holding.candlestick_pattern !== 'no_clear_pattern' && (
                                    <span className="text-[8px] text-slate-500 mt-0.5 max-w-[80px] truncate" title={holding.candlestick_pattern}>
                                      {holding.candlestick_pattern.split(',')[0].replace(/_/g, ' ')}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-3 px-3 text-center">
                                <div className="flex flex-col items-center">
                                  <span className="font-medium text-cyan-700">${holding.analyst_target?.toFixed?.(0) || "0"}</span>
                                  <span className={`text-[10px] ${holding.analyst_implied_return >= 40 ? 'text-green-600' : 'text-cyan-600'}`}>
                                    +{holding.analyst_implied_return?.toFixed?.(0) || "0"}%
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-3 text-center">
                                <span className={`font-semibold ${
                                  holding.historical_1y_return >= 40 ? 'text-green-600' : 
                                  holding.historical_1y_return >= 20 ? 'text-blue-600' : 
                                  holding.historical_1y_return >= 0 ? 'text-slate-600' : 'text-red-600'
                                }`}>
                                  {holding.historical_1y_return > 0 ? '+' : ''}{holding.historical_1y_return?.toFixed?.(0) || "0"}%
                                </span>
                              </td>
                            </>
                          )}
                          <td className="py-3 px-3 text-center">
                            {holding.shares}
                          </td>
                          <td className="py-3 px-3 text-center font-semibold">
                            ${holding.value?.toLocaleString()}
                          </td>
                          <td className={`py-3 px-3 text-center hidden lg:table-cell ${getChangeColor(holding.day_change)}`}>
                            {holding.day_change > 0 ? '+' : ''}{holding.day_change?.toFixed?.(2) || "0.00"}%
                          </td>
                          {portfolio.methodology && (
                            <td className="py-3 px-3 text-center hidden xl:table-cell">
                              <div className="flex flex-col items-center">
                                <span className={`text-xs font-medium ${
                                  holding.momentum_20d > 5 ? 'text-green-600' : 
                                  holding.momentum_20d > 0 ? 'text-blue-600' : 'text-red-600'
                                }`}>
                                  {holding.momentum_20d > 0 ? '+' : ''}{holding.momentum_20d?.toFixed?.(1) || "0"}%
                                </span>
                                <span className="text-[9px] text-slate-400">20D</span>
                              </div>
                            </td>
                          )}
                          <td className="py-3 px-3 text-center" data-testid={`pd-option-${holding.ticker}`}>
                            {holding.options_overlay ? (
                              <div
                                className="leading-tight cursor-help"
                                title={
                                  `${holding.options_overlay.expiration} · $${holding.options_overlay.strike} CALL\n` +
                                  `Mid $${holding.options_overlay.mid_price} (bid $${holding.options_overlay.bid} / ask $${holding.options_overlay.ask}, spread ${holding.options_overlay.spread_pct}%)\n` +
                                  `Δ ${holding.options_overlay.delta} · IV ${holding.options_overlay.iv_pct}%\n` +
                                  `Vol ${holding.options_overlay.volume?.toLocaleString()} · OI ${holding.options_overlay.open_interest?.toLocaleString()}\n` +
                                  `Leverage ~${holding.options_overlay.leverage_x}× · Breakeven $${holding.options_overlay.breakeven_price}\n` +
                                  `1 contract ≈ $${holding.options_overlay.contract_cost?.toLocaleString()}`
                                }
                              >
                                <div className="font-mono text-emerald-700 text-xs font-bold">
                                  ${holding.options_overlay.strike}c {holding.options_overlay.expiration?.slice(5)}
                                </div>
                                <div className="text-[9px] text-slate-500 font-mono">
                                  Δ{holding.options_overlay.delta} · ${holding.options_overlay.mid_price} · V{(holding.options_overlay.volume || 0).toLocaleString()}
                                </div>
                              </div>
                            ) : (
                              <span className="text-[10px] text-slate-400 italic">no liquid call</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Rebalancing Schedule */}
            <Card className="border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-blue-800">
                  <Calendar className="w-5 h-5" />
                  Rebalancing Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-blue-800">Daily Check</span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Monitor positions for momentum shifts, sector rotations, and news-driven opportunities.
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-green-800">Weekly Rebalance</span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Adjust weights if any position deviates &gt;5% from target. Lock in gains from outperformers.
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Rocket className="w-4 h-4 text-purple-600" />
                      <span className="font-semibold text-purple-800">Monthly Review</span>
                    </div>
                    <p className="text-xs text-slate-600">
                      Full portfolio reassessment. Replace underperformers. Add new high-conviction picks.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Risk Factors */}
            <Card className="border-yellow-200 bg-gradient-to-br from-yellow-50 to-orange-50">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg text-yellow-800">
                  <AlertTriangle className="w-5 h-5" />
                  Risk Factors & Considerations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {portfolio.risk_factors?.map((factor, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-white rounded-lg border border-yellow-200">
                      <XCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-600">{factor}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Disclaimer */}
            <div className="p-4 bg-slate-100 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                <strong>Disclaimer:</strong> This AI-generated portfolio is for informational purposes only and does not constitute financial advice. 
                Past performance is not indicative of future results. A 40%+ annual return carries significant risk. 
                Always consult with a qualified financial advisor before making investment decisions.
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PortfolioDesignerPage;
